import React, { useMemo, useEffect, useState } from 'react';
import { IconExclamationTriangle } from '../ui-kit/icons';
import styled from 'styled-components';
import lodash, { isEqual } from 'lodash';
import { Modal, Button } from '../ui-kit';
import { connect } from 'react-redux';
import { operations as skillOperations } from '../../state/modules/skills';
import { IncidentContainer } from '../general/UnderstaffingWarning';
import moment from 'moment';
import Beeper from '../general/Beeper';

const StyledModal = styled(Modal)`
  .modal-content {
    background: #cc1c1c;
  }
`;

const WARNING_DELAY = 60 * 1000;
const IncidentUnderstaffingPopup = ({ groups, incidents, skills, fetchSkills, audibleWarningEnabled }) => {
  const [dismissedIncidentIds, setDismissedIncidentIds] = useState([]);
  const [minCreatedAt, setMinCreatedAt] = useState(Date.now() - WARNING_DELAY);

  // incidents is a data structure of group_id -> incident_id -> incident. For
  // simplity, we flatten this into a plain list of incidents.
  const incidentList = useMemo(
    () => Object.values(incidents).flatMap(incidentsById => Object.values(incidentsById)),
    [incidents]
  );

  // force update every 5 seconds to recompute incidentsToDisplay. This is
  // necessary because incidents might be known, but not displayed yet because
  // the warningDelay has not passed yet. There won't be a state update when the
  // warningDelay has passed.
  useEffect(() => {
    const interval = setInterval(() => setMinCreatedAt(Date.now() - WARNING_DELAY), 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  const dismiss = () => {
    setDismissedIncidentIds([...dismissedIncidentIds, ...incidentsToDisplay.map(i => i.id)]);
  };

  const nonDismissedIncidentsWithUnderstaffing = useMemo(() => {
    const result = incidentList.filter(
      incident =>
        !dismissedIncidentIds.includes(incident.id) &&
        incident.warning_statuses.some(ws => ws.warning_level === 'below_required')
    );

    return lodash.uniqBy(result, 'id');
  }, [incidentList, dismissedIncidentIds]);

  // further filters nonDismissedIncidentsWithUnderstaffing respecting the
  // warning delay for each incident. Incidents can have to responder modes:
  // 1. users are only counted when they have explicitly accepted the incident
  // 2. users are counted if they are available in the rota, and have not
  //    rejected
  // In the former mode, it can take a while before users have responded. We
  // need to wait 60 seconds before alerting in case of understaffing.
  const incidentsToDisplay = useMemo(
    () => nonDismissedIncidentsWithUnderstaffing.filter(i => moment(i.created_at).isBefore(minCreatedAt)),
    [nonDismissedIncidentsWithUnderstaffing, minCreatedAt]
  );

  // Returns a list of ids of incidents that are not understaffed
  const nonUnderstaffedIncidentIds = useMemo(
    () => incidentList.filter(incident => incident.warning_statuses.every(ws => ws.warning_level !== 'below_required')),
    [incidentList]
  );

  useEffect(() => {
    // undismiss the incidents that no longer have understaffing. We might need
    // to show them again.
    setDismissedIncidentIds(oldDismissedIncidentIds => {
      const dismissedIncidentIds = lodash.difference(oldDismissedIncidentIds, nonUnderstaffedIncidentIds);
      return isEqual(oldDismissedIncidentIds, dismissedIncidentIds) ? oldDismissedIncidentIds : dismissedIncidentIds;
    });
  }, [nonUnderstaffedIncidentIds]);

  if (!groups || !skills.lastFetch || incidentsToDisplay.length === 0) return null;

  return (
    <StyledModal show={true} size="lg">
      {audibleWarningEnabled && <Beeper />}
      <Modal.Header>
        <Modal.Title>
          <h1 className="text-light">
            <IconExclamationTriangle /> {I18n.t('understaffing').toUpperCase()}
          </h1>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {incidentsToDisplay.map(incident => (
          <IncidentContainer key={incident.id} incident={incident} skills={skills} groups={groups} />
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={dismiss}>
          {I18n.t('close')}
        </Button>
      </Modal.Footer>
    </StyledModal>
  );
};

const mapStateToProps = state => ({
  skills: state.skills
});

const mapDispatchToProps = {
  fetchSkills: skillOperations.fetch
};

export { IncidentUnderstaffingPopup };

export default connect(mapStateToProps, mapDispatchToProps)(IncidentUnderstaffingPopup);
